import './QuantitativeChart.css';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { renderToString } from 'react-dom/server';
import { calculateMinMaxValues, calculatePaddedDates, higherRangeQuantitativeObjectiveFormatter, lowerRangeQuantitativeObjectiveFormatter, regularQuantitativeObjectiveFormatter, renderSampleTooltip, thresholdQuantitativeObjectiveFormatter } from '../helper';
import { getObjectiveType } from '../../objectives/QuantitativeObjectiveEditForm';
import { getMissedDateSeries, getObjectivesSeries, getSamplesSeries } from '../optionsHelper';
import B2BChart from '../B2BChart';

const QuantitativeChart = ({ indicatorDTO, objective }) => {

    const objectiveType = getObjectiveType(objective);

    const paddingPercentage = 0.05; // 5% padding
    const { paddedStart, paddedEnd } = (objectiveType === 'Regular' || objectiveType === 'Range') ?
        calculatePaddedDates(objective.startDate, objective.endDate, paddingPercentage) :
        (objectiveType === 'Threshold_Above' || objectiveType === 'Threshold_Below') ?
            calculatePaddedDates(objective.startDate, objective.endDate, paddingPercentage) :
            { paddedStart: objective.startDate, paddedEnd: objective.endDate };

    const samplesData = objective.samplesPerObjective.map(sample => ({
        value: [sample.sampleDate, sample.quantitativeValue],
        status: sample.status,
        trend: sample.trend
    }));
    const sampleValues = samplesData.map(sample => sample.value[1]);
    const { minValue, maxValue } = (objectiveType === 'Regular' || objectiveType === 'Range') ?
        calculateMinMaxValues(objective.startQuantitativeValue, objective.endQuantitativeValue, sampleValues, paddingPercentage) :
        (objectiveType === 'Threshold_Above' || objectiveType === 'Threshold_Below') ?
            calculateMinMaxValues(objective.startQuantitativeValue, objective.startQuantitativeValue, sampleValues, paddingPercentage) :
            { minValue: objective.startQuantitativeValue, maxValue: objective.startQuantitativeValue };

    const missedDateSample = objective.missedExpectedSampleDate;

    const options = {
        grid: {
            top: '5%',
            left: '8%',
            right: '5%',
            bottom: '7%'
        },
        xAxis: {
            type: 'time',
            min: paddedStart,
            max: paddedEnd
        },
        yAxis: {
            type: 'value',
            min: minValue,
            max: maxValue
        },
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                const seriesName = params.seriesName;
                if (seriesName === 'Quantitative Objective') {
                    return regularQuantitativeObjectiveFormatter(params, objective, indicatorDTO);
                } else if (seriesName === 'Quantitative Objective Threshold') {
                    return thresholdQuantitativeObjectiveFormatter(params, objective, indicatorDTO);
                } else if (seriesName === 'Quantitative Objective Lower Range') {
                    return lowerRangeQuantitativeObjectiveFormatter(params, objective, indicatorDTO);
                } else if (seriesName === 'Quantitative Objective Upper Range') {
                    return higherRangeQuantitativeObjectiveFormatter(params, objective, indicatorDTO);
                } else if (seriesName === 'Objective Samples') {
                    const date = new Date(params.data.value[0]).toLocaleDateString();
                    const value = params.data.value[1];
                    const status = params.data.status;
                    const trend = params.data.trend;

                    return renderToString(renderSampleTooltip(date, value, status, trend, indicatorDTO));
                }
                return '';
            }
        },
        series: [
            getSamplesSeries(samplesData),
            ...getObjectivesSeries(objective, objectiveType),
            ...getMissedDateSeries(missedDateSample, minValue, maxValue),
        ]
    };

    return (
        <B2BChart
            options={options}
            enableZoom={true}
            className='quantitative-chart'
        />
    );
};
QuantitativeChart.propTypes = {
    indicatorDTO: PropTypes.object.isRequired,
    objective: PropTypes.shape({
        objectiveType: PropTypes.oneOf(['QUANTITATIVE', 'QUANTITATIVE_THRESHOLD']).isRequired,
    }).isRequired,
};

export default QuantitativeChart;